import React from 'react';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { eventManager } from 'ats/utils/EventManager';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import { ApplicantTaskEdit } from 'ats/pages/applicants/components/ApplicantTaskEdit';
import { JobApplicationTaskEdit } from 'ats/pages/pipeline/components/JobApplicationTaskEdit';
import { PIN_SCOPES, getPinMenuItem } from 'ats/utils/taskPin';

const getApplicantId = task => task.applicantId ?? task.applicant?.id;

const getJobApplicationId = task =>
	task.jobApplicationId ?? task.jobApplication?.id;

function updateStatus({ scope, task, status, onSuccess }) {
	const request =
		scope === PIN_SCOPES.JOB
			? jobApplicationStore.updateTask(getJobApplicationId(task), task.id, {
					jobApplicationTask: { status }
				})
			: applicantStore.updateTask(getApplicantId(task), task.id, {
					applicantTask: { status }
				});

	return request.then(result => {
		if (result.status === 'success') {
			notificationStore.pushSuccessNotification(
				'Task Status Updated',
				'The Task status has been successfully updated.'
			);
			eventManager.emit('taskChanged');
			onSuccess?.();
		} else {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update the Task status'
			);
		}
	});
}

function openEdit({ scope, task, onSuccess }) {
	modalStore.openModal(
		{
			title: 'Update Task',
			size: 'xl',
			position: 'center',
			saveButtonLabel: 'Update'
		},
		scope === PIN_SCOPES.JOB ? (
			<JobApplicationTaskEdit
				jobApplicationId={getJobApplicationId(task)}
				task={task}
				handleSuccess={async () => {
					onSuccess?.();
				}}
			/>
		) : (
			<ApplicantTaskEdit
				applicant={task.applicant ?? { id: getApplicantId(task) }}
				task={task}
				handleSuccess={async () => {
					onSuccess?.();
				}}
			/>
		)
	);
}

function confirmDelete({ scope, task, onSuccess }) {
	dialogStore.openDialog(
		{
			title: 'Delete Task',
			message: 'This Task entry will be deleted. Are you sure?',
			confirmLabel: 'Delete',
			confirmButtonClass: 'is-danger'
		},
		() =>
			(scope === PIN_SCOPES.JOB
				? jobApplicationStore.deleteTask(getJobApplicationId(task), task.id)
				: applicantStore.deleteTask(getApplicantId(task), task.id)
			).then(result => {
				if (result.status === 'success') {
					notificationStore.pushSuccessNotification(
						'Task Deleted',
						'The Task has been successfully deleted.'
					);
					eventManager.emit('taskChanged');
					onSuccess?.();
				} else {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete the Task'
					);
				}
			})
	);
}

export function getTaskMenuItems({ scope, task, onSuccess }) {
	const context = { scope, task, onSuccess };
	return [
		{
			label: 'Mark as Completed',
			img: '/ats/icons/check-icon.svg',
			onClick: () => updateStatus({ ...context, status: 'completed' })
		},
		{
			label: 'Mark as Cancelled',
			img: '/ats/icons/close-2-icon.svg',
			onClick: () => updateStatus({ ...context, status: 'cancelled' })
		},
		{
			label: 'Mark as Pending',
			img: '/ats/icons/clock-2-icon.svg',
			onClick: () => updateStatus({ ...context, status: 'pending' })
		},
		getPinMenuItem(context),
		{
			label: 'Edit',
			img: '/ats/icons/edit-icon.svg',
			onClick: () => openEdit(context)
		},
		{
			label: <span style={{ color: '#E31B54' }}>Delete</span>,
			img: '/ats/icons/trash-icon.svg',
			onClick: () => confirmDelete(context)
		}
	].filter(Boolean);
}
